import { Injectable, inject } from '@angular/core';
import { BackgroundRenderClientService } from '../../../core/render-runtime/worker/background-render-client.service';
import { RenderJob, RenderJobResult } from '../../../core/render-runtime/worker/render-job.model';
import { ReviewSocialTargetComplete } from '../../Review/services/review-post-generation-api.service';
import { TemplateReviewUploadService } from './template-review-upload.service';

/**
 * SocialTemplateRenderHandoffService — Template-Use render → Review upload.
 *
 * The actual video encode runs on the unified background render worker
 * (`BackgroundRenderClientService`), the SAME engine the Review editor and the
 * Processing flow use. The caller builds a serializable `RenderJob` (via the
 * super-editor `buildEditorRenderJob` adapter — Template-Use already has a full
 * `EditorProject`) and hands it here; this service drives the worker and runs
 * the unchanged reserve → sign → upload sequence the Review intake expects.
 *
 * The legacy synthetic on-page renderer (`editor-timeline-renderer`) is no
 * longer used by this path.
 */
@Injectable({ providedIn: 'root' })
export class SocialTemplateRenderHandoffService {
  private readonly reviewUpload = inject(TemplateReviewUploadService);
  private readonly backgroundRender = inject(BackgroundRenderClientService);

  /**
   * Renders a prebuilt `RenderJob` on the background worker and uploads the
   * result as a new Review item.
   *
   * Pipeline (reserve/sign/upload identical to the previous synthetic path):
   *   1. prepare           — reserve the Review folder + register script/title.
   *   2. background worker  — encode the MP4 + cover thumbnail off-thread.
   *   3. signRenderUpload   — signed upload URLs for video + thumbnail.
   *   4. uploadRenderedAndComplete — PUT the blobs, complete → { reviewId, ... }.
   */
  async renderAndUploadToReview(request: {
    postId: string;
    job: RenderJob;
    templateId?: string | null;
    name: string;
    durationSeconds: number;
    caption?: string;
    script: string;
    onProgress?: (percent: number, message: string) => void;
    /**
     * Wave 5 (cancel): receives the background-worker cancel function once the render starts, so the
     * Jivu Command driver can ABORT the in-flight encode. Optional — the inline modal path never sets
     * it, so its behaviour is unchanged.
     */
    onHandle?: (cancel: () => void) => void;
  }): Promise<ReviewSocialTargetComplete> {
    const script = request.script.trim();
    if (!script) {
      throw new Error('Script is required before rendering a template to Review.');
    }
    if (!this.backgroundRender.supported()) {
      throw new Error('This browser cannot render video (WebCodecs/Worker unavailable).');
    }

    request.onProgress?.(5, 'Preparing Review folder...');
    const prepared = await this.reviewUpload.prepare(request.postId, {
      script,
      title: request.name,
      caption: request.caption,
      templateId: request.templateId,
      durationSeconds: request.durationSeconds
    });

    const rendered = await this.renderWithWorker(request.job, request.onProgress, request.onHandle);

    const videoContentType = normalizeUploadContentType(rendered.mimeType, 'video/mp4');
    const thumbnailBlob = rendered.thumbnails?.[0] ?? null;
    const thumbnailContentType = normalizeUploadContentType(thumbnailBlob?.type, 'image/jpeg');

    request.onProgress?.(90, 'Requesting upload URLs...');
    const uploadPlan = await this.reviewUpload.signRenderUpload(request.postId, prepared.targetId, {
      videoContentType,
      thumbnailContentType
    });

    request.onProgress?.(94, 'Uploading to Review...');
    return this.reviewUpload.uploadRenderedAndComplete(
      request.postId,
      uploadPlan,
      rendered.blob,
      thumbnailBlob
    );
  }

  /** Drives the background worker, mapping its phase/progress into the template loader scale (8–86%). */
  private async renderWithWorker(
    job: RenderJob,
    onProgress?: (percent: number, message: string) => void,
    onHandle?: (cancel: () => void) => void
  ): Promise<RenderJobResult> {
    onProgress?.(8, 'Preparing template timeline...');
    const handle = this.backgroundRender.start(job, p => {
      const span = Math.max(0, Math.min(1, p.progress || 0));
      const percent = Math.min(86, Math.round(8 + span * 78));
      const message = p.phase === 'encoding' || p.phase === 'rendering'
        ? 'Rendering template on this device...'
        : (p.message || 'Preparing template timeline...');
      onProgress?.(percent, message);
    });
    // Hand the worker's cancel up to the caller so a Jivu Command cancel can abort this encode.
    onHandle?.(handle.cancel);
    return handle.result;
  }
}

function normalizeUploadContentType(value: string | null | undefined, fallback: string): string {
  const raw = (value || fallback).trim();
  const mediaType = raw.split(';', 1)[0].trim().toLowerCase();
  if (mediaType === 'image/jpg') return 'image/jpeg';
  if (mediaType === 'video/mp4' || mediaType === 'video/webm' || mediaType === 'image/jpeg' || mediaType === 'image/png') {
    return mediaType;
  }
  return raw || fallback;
}
